import type { AiConversationThread } from "@salon/types/ai-agent";
import { formatDistanceToNow } from "date-fns";
import { Archive, Clock, MessageSquare, Search, Trash2 } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { aiAgentClient } from "../api/ai-agent-client";

interface ThreadListProps {
  salonId: string;
  onSelectThread: (threadId: string) => void;
  onNewThread: () => void;
  currentThreadId?: string;
}

export function ThreadList({
  salonId,
  onSelectThread,
  onNewThread: _onNewThread,
  currentThreadId,
}: ThreadListProps) {
  const { t } = useTranslation("ai-chat");
  const [threads, setThreads] = useState<AiConversationThread[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [hasMore, setHasMore] = useState(false);

  const loadThreads = useCallback(async () => {
    try {
      setLoading(true);
      const response = await aiAgentClient.listThreads(salonId, {
        status: "active",
        search: searchQuery || undefined,
        limit: 20,
        offset: 0,
      });
      setThreads(response.threads);
      setHasMore(response.hasMore);
    } catch (error) {
      console.error("Failed to load threads:", error);
    } finally {
      setLoading(false);
    }
  }, [salonId, searchQuery]);

  // Load threads
  useEffect(() => {
    loadThreads();
  }, [loadThreads]);

  const handleArchive = async (threadId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await aiAgentClient.archiveThread(salonId, threadId);
      loadThreads();
    } catch (error) {
      console.error("Failed to archive thread:", error);
    }
  };

  const handleDelete = async (threadId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!confirm(t("threadList.deleteConfirm"))) {
      return;
    }
    try {
      await aiAgentClient.deleteThread(salonId, threadId);
      loadThreads();
    } catch (error) {
      console.error("Failed to delete thread:", error);
    }
  };

  return (
    <div className="flex h-full flex-col bg-white dark:bg-gray-900">
      {/* Search */}
      <div className="border-b border-gray-200 p-4 dark:border-gray-700">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder={t("threadList.searchPlaceholder")}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full rounded-lg border border-gray-300 py-2 pl-10 pr-4 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-800 dark:text-white"
          />
        </div>
      </div>

      {/* Thread List */}
      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="p-4 text-center text-sm text-gray-500">{t("threadList.loading")}</div>
        ) : threads.length === 0 ? (
          <div className="p-4 text-center text-sm text-gray-500">
            {searchQuery ? t("threadList.noResults") : t("threadList.empty")}
          </div>
        ) : (
          <div className="divide-y divide-gray-200 dark:divide-gray-700">
            {threads.map((thread) => (
              <button
                key={thread.id}
                type="button"
                onClick={() => onSelectThread(thread.id)}
                className={`group w-full cursor-pointer p-4 text-left transition-colors hover:bg-gray-50 dark:hover:bg-gray-800 ${
                  currentThreadId === thread.id ? "bg-blue-50 dark:bg-blue-900/20" : ""
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="min-w-0 flex-1">
                    <h3 className="truncate text-sm font-medium text-gray-900 dark:text-white">
                      {thread.title}
                    </h3>
                    <div className="mt-2 flex items-center gap-3 text-xs text-gray-500 dark:text-gray-500">
                      <span className="flex items-center gap-1">
                        <MessageSquare className="h-3 w-3" />
                        {thread.messageCount}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {formatDistanceToNow(new Date(thread.lastMessageAt), {
                          addSuffix: true,
                        })}
                      </span>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="ml-2 flex gap-1 opacity-0 transition-opacity group-hover:opacity-100">
                    <button
                      type="button"
                      onClick={(e) => handleArchive(thread.id, e)}
                      className="rounded p-1 text-gray-400 hover:bg-gray-200 hover:text-gray-600 dark:hover:bg-gray-700 dark:hover:text-gray-300"
                      title="Archive"
                    >
                      <Archive className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      onClick={(e) => handleDelete(thread.id, e)}
                      className="rounded p-1 text-gray-400 hover:bg-red-100 hover:text-red-600 dark:hover:bg-red-900/20 dark:hover:text-red-400"
                      title="Delete"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Load More */}
      {hasMore && (
        <div className="border-t border-gray-200 p-4 dark:border-gray-700">
          <button
            type="button"
            onClick={loadThreads}
            className="w-full rounded-lg border border-gray-300 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-800"
          >
            {t("threadList.loadMore")}
          </button>
        </div>
      )}
    </div>
  );
}
